/**
 * The routing shell.
 *
 * Small on purpose. This app has two routes today — the sign-in screen and the
 * monitor list — and REQ-403 will add `/monitors/:id` next. A router library
 * would be four times the size of what it replaces, and this repo's whole
 * dependency posture is that a thing is added when it is used rather than in
 * anticipation.
 *
 * Real URLs rather than a hash: the deploy serves the bundle through Caddy with
 * `try_files {path} /index.html`, so a deep link to `/monitors` is served the
 * app and resolved here. That is already how the site is configured; nothing
 * about this file assumes it beyond the fallback below.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

/** Every path this app answers on. Unknown paths resolve to one of these. */
export type RouteName = "login" | "monitors";

export interface Route {
  readonly name: RouteName;
  readonly path: string;
}

export const ROUTE_PATHS: Readonly<Record<RouteName, string>> = {
  login: "/login",
  monitors: "/monitors",
};

/**
 * Resolve a pathname to a route.
 *
 * Anything unrecognised — including `/` — resolves to `monitors`, and the app
 * shell sends an unauthenticated visitor from there to `login`. That ordering
 * matters: it is what makes "land on the login screen, never a blank page"
 * true for every URL rather than only for the two spelled out above.
 */
export function routeForPath(pathname: string): Route {
  const normalised = pathname.replace(/\/+$/, "") || "/";
  if (normalised === ROUTE_PATHS.login) return { name: "login", path: ROUTE_PATHS.login };
  return { name: "monitors", path: ROUTE_PATHS.monitors };
}

export interface Router {
  readonly route: Route;
  /** Push a route and re-render. A no-op when already there, so it is safe in an effect. */
  navigate(name: RouteName): void;
}

export function useRouter(): Router {
  const [pathname, setPathname] = useState<string>(() =>
    typeof window === "undefined" ? "/" : window.location.pathname,
  );

  useEffect(() => {
    const onPopState = (): void => {
      setPathname(window.location.pathname);
    };
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, []);

  const navigate = useCallback((name: RouteName) => {
    const next = ROUTE_PATHS[name];
    if (window.location.pathname !== next) {
      window.history.pushState(null, "", next);
    }
    setPathname(next);
  }, []);

  const route = useMemo(() => routeForPath(pathname), [pathname]);

  return { route, navigate };
}
